/**
 * Custom 2×2 Matrix Tool
 *
 * Lets users create their own 2×2 prioritization matrix:
 *   1. Name it and define axes + quadrant labels
 *   2. Choose save scope: local / workspace / org
 *   3. Run it exactly like Eisenhower or Impact/Effort
 *
 * Saved templates appear in the landing view for reuse.
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Trash2,
  GripVertical,
  CheckCircle2,
  Plus,
  LayoutGrid,
  Pencil,
  ChevronRight,
  Globe2,
  Building2,
  HardDrive,
  ArrowLeft,
  X,
  RefreshCw,
  Info,
} from 'lucide-react';
import ToolWrapper, {
  type ToolHostProps,
  type ToolResultItem,
} from '../../components/prioritization/ToolWrapper';
import { useToolSituationalContext, type ToolItem } from '../../contexts/toolItemsHooks';
import {
  useCustomMatrixStore,
  buildDefaultQuadrants,
  type CustomMatrixTemplate,
  type MatrixSaveScope,
  type QuadrantDefinition,
} from '../../stores/prioritization/customMatrixStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';

// ============================================================================
// Task item types
// ============================================================================

type QuadrantKey = string | null; // e.g. 'top-left', 'top-right', etc.

interface TaskItem {
  id: string;
  title: string;
  description?: string;
  quadrant: QuadrantKey;
  sourceId?: string;
}

// ============================================================================
// Colour palette options for quadrant builder
// ============================================================================

const COLOUR_OPTIONS = [
  { label: 'Green',  color: 'bg-[var(--green-dim)]',  border: 'border-[var(--green)]',  headerBg: 'bg-[var(--green-dim)]',  preview: 'bg-[var(--green)]'  },
  { label: 'Blue',   color: 'bg-[var(--slate-dim)]',   border: 'border-[var(--slate)]',   headerBg: 'bg-[var(--slate-dim)]',   preview: 'bg-[var(--slate)]'   },
  { label: 'Red',    color: 'bg-[var(--coral-dim)]',    border: 'border-[var(--coral)]',    headerBg: 'bg-[var(--coral-dim)]',    preview: 'bg-[var(--coral)]'    },
  { label: 'Yellow', color: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', headerBg: 'bg-[var(--mustard-dim)]', preview: 'bg-[var(--mustard)]' },
  { label: 'Purple', color: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', headerBg: 'bg-[var(--slate-dim)]', preview: 'bg-[var(--slate)]' },
  { label: 'Orange', color: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', headerBg: 'bg-[var(--mustard-dim)]', preview: 'bg-[var(--mustard)]' },
  { label: 'Teal',   color: 'bg-[var(--green-dim)]',   border: 'border-[var(--green)]',   headerBg: 'bg-[var(--green-dim)]',   preview: 'bg-[var(--green)]'   },
  { label: 'Gray',   color: 'bg-[var(--surface)]',   border: 'border-[var(--rule)]',   headerBg: 'bg-[var(--hover-tint)]',   preview: 'bg-[var(--surface)]'   },
];

const QUADRANT_POSITIONS: Array<{
  position: QuadrantDefinition['position'];
  label: string;
  corner: string;
}> = [
  { position: 'top-left',     label: 'Top-Left',     corner: 'High Y / Low X'  },
  { position: 'top-right',    label: 'Top-Right',    corner: 'High Y / High X' },
  { position: 'bottom-left',  label: 'Bottom-Left',  corner: 'Low Y / Low X'   },
  { position: 'bottom-right', label: 'Bottom-Right', corner: 'Low Y / High X'  },
];

// ============================================================================
// Scope selector component
// ============================================================================

function ScopeSelector({
  value,
  onChange,
}: {
  value: MatrixSaveScope;
  onChange: (scope: MatrixSaveScope) => void;
}) {
  const options: Array<{
    scope: MatrixSaveScope;
    icon: React.ReactNode;
    label: string;
    description: string;
  }> = [
    {
      scope: 'local',
      icon: <HardDrive className="h-5 w-5 text-[var(--text-ter)]" />,
      label: 'Personal',
      description: 'Private to you. Saved in your browser.',
    },
    {
      scope: 'workspace',
      icon: <Globe2 className="h-5 w-5 text-[var(--text-ter)]" />,
      label: 'Workspace',
      description: 'All members of your current workspace can see and use this matrix.',
    },
    {
      scope: 'org',
      icon: <Building2 className="h-5 w-5 text-[var(--text-ter)]" />,
      label: 'Organization',
      description: 'Available across every workspace in your organization.',
    },
  ];

  return (
    <div className="space-y-2">
      {options.map(opt => (
        <button
          key={opt.scope}
          type="button"
          onClick={() => onChange(opt.scope)}
          className={`w-full flex items-start gap-3 p-3  border-2 cursor-pointer transition-colors text-left ${
            value === opt.scope
              ? 'border-consulting-slate-blue-400 bg-consulting-slate-blue-50 dark:bg-consulting-slate-blue-900/20 dark:border-consulting-slate-blue-500'
              : 'border-[var(--rule)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)] bg-[var(--bg-raised)]'
          }`}
        >
          <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
            value === opt.scope
              ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-500'
              : 'border-[var(--rule)]'
          }`}>
            {value === opt.scope && (
              <div className="h-1.5 w-1.5 rounded-full bg-[var(--bg-raised)]" />
            )}
          </div>
          <div className="flex items-start gap-2 flex-1">
            <div className="mt-0.5 flex-shrink-0">{opt.icon}</div>
            <div>
              <div className={`font-medium text-sm ${value === opt.scope ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>
                {opt.label}
              </div>
              <div className="text-xs text-[var(--text-ter)] mt-0.5">{opt.description}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Matrix Builder wizard
// ============================================================================

interface BuilderProps {
  onSave: (template: CustomMatrixTemplate) => void;
  onCancel: () => void;
  editTemplate?: CustomMatrixTemplate;
  workspaceId?: string;
  orgId?: string;
  userId?: string;
}

function MatrixBuilder({ onSave, onCancel, editTemplate, workspaceId, orgId, userId }: BuilderProps) {
  const createTemplate = useCustomMatrixStore(s => s.createTemplate);
  const updateTemplate = useCustomMatrixStore(s => s.updateTemplate);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: basics
  const [name, setName] = useState(editTemplate?.name ?? '');
  const [description, setDescription] = useState(editTemplate?.description ?? '');
  const [xAxisLabel, setXAxisLabel] = useState(editTemplate?.xAxisLabel ?? 'X Axis');
  const [xLow, setXLow] = useState(editTemplate?.xAxisLowLabel ?? 'Low');
  const [xHigh, setXHigh] = useState(editTemplate?.xAxisHighLabel ?? 'High');
  const [yAxisLabel, setYAxisLabel] = useState(editTemplate?.yAxisLabel ?? 'Y Axis');
  const [yLow, setYLow] = useState(editTemplate?.yAxisLowLabel ?? 'Low');
  const [yHigh, setYHigh] = useState(editTemplate?.yAxisHighLabel ?? 'High');

  // Step 2: quadrants
  const [quadrants, setQuadrants] = useState<QuadrantDefinition[]>(
    editTemplate?.quadrants ?? buildDefaultQuadrants('High', 'Low', 'High', 'Low'),
  );

  // Step 3: scope
  const [scope, setScope] = useState<MatrixSaveScope>(editTemplate?.scope ?? 'local');

  // Auto-regenerate quadrant names when axis labels change (only if user hasn't customised them)
  const [autoNames, setAutoNames] = useState(!editTemplate);
  useEffect(() => {
    if (!autoNames) return;
    setQuadrants(buildDefaultQuadrants(xHigh, xLow, yHigh, yLow));
  }, [xHigh, xLow, yHigh, yLow, autoNames]);

  const updateQuadrant = (position: QuadrantDefinition['position'], field: keyof QuadrantDefinition, val: string) => {
    setAutoNames(false);
    setQuadrants(prev =>
      prev.map(q => (q.position === position ? { ...q, [field]: val } : q)),
    );
  };

  const setQuadrantColor = (position: QuadrantDefinition['position'], colorOpt: typeof COLOUR_OPTIONS[number]) => {
    setQuadrants(prev =>
      prev.map(q =>
        q.position === position
          ? { ...q, color: colorOpt.color, border: colorOpt.border, headerBg: colorOpt.headerBg }
          : q,
      ),
    );
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      if (editTemplate) {
        await updateTemplate(editTemplate.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          xAxisLabel,
          xAxisLowLabel: xLow,
          xAxisHighLabel: xHigh,
          yAxisLabel,
          yAxisLowLabel: yLow,
          yAxisHighLabel: yHigh,
          quadrants,
          scope,
          workspaceId: scope !== 'local' ? workspaceId : undefined,
          orgId: scope === 'org' ? orgId : undefined,
        });
        onSave({ ...editTemplate, name: name.trim(), quadrants, scope });
      } else {
        const created = await createTemplate({
          name: name.trim(),
          description: description.trim() || undefined,
          xAxisLabel,
          xAxisLowLabel: xLow,
          xAxisHighLabel: xHigh,
          yAxisLabel,
          yAxisLowLabel: yLow,
          yAxisHighLabel: yHigh,
          quadrants,
          scope,
          createdBy: userId,
          workspaceId: scope !== 'local' ? workspaceId : undefined,
          orgId: scope === 'org' ? orgId : undefined,
        });
        onSave(created);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const step1Valid = name.trim().length > 0;
  const step2Valid = quadrants.every(q => q.name.trim().length > 0);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {[
          { n: 1, label: 'Axes & Labels' },
          { n: 2, label: 'Quadrants' },
          { n: 3, label: 'Save As' },
        ].map(({ n, label }) => (
          <div key={n} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                step === n
                  ? 'bg-consulting-slate-blue-600 text-white'
                  : step > n
                  ? 'bg-[var(--green)] text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}
            >
              {step > n ? '✓' : n}
            </div>
            <span className={`text-sm ${step === n ? 'font-semibold text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
              {label}
            </span>
            {n < 3 && <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
          </div>
        ))}
      </div>

      {/* ── Step 1: Axes & Name ──────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Matrix Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Risk vs Value, Complexity vs Impact"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does this matrix help decide?"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* X Axis */}
            <div className="p-4 border border-[var(--rule)] space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-6 rounded bg-[var(--slate)]" />
                <p className="font-medium text-sm text-[var(--text-pri)]">Horizontal Axis (X)</p>
              </div>
              <div>
                <label className="block text-xs text-[var(--text-ter)] mb-1">Axis Label</label>
                <input
                  type="text"
                  value={xAxisLabel}
                  onChange={e => setXAxisLabel(e.target.value)}
                  placeholder="e.g. Effort"
                  className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">← Left</label>
                  <input
                    type="text"
                    value={xLow}
                    onChange={e => setXLow(e.target.value)}
                    placeholder="Low"
                    className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">Right →</label>
                  <input
                    type="text"
                    value={xHigh}
                    onChange={e => setXHigh(e.target.value)}
                    placeholder="High"
                    className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                  />
                </div>
              </div>
            </div>

            {/* Y Axis */}
            <div className="p-4 border border-[var(--rule)] space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-1.5 rounded bg-[var(--slate)]" />
                <p className="font-medium text-sm text-[var(--text-pri)]">Vertical Axis (Y)</p>
              </div>
              <div>
                <label className="block text-xs text-[var(--text-ter)] mb-1">Axis Label</label>
                <input
                  type="text"
                  value={yAxisLabel}
                  onChange={e => setYAxisLabel(e.target.value)}
                  placeholder="e.g. Value"
                  className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">↑ Top</label>
                  <input
                    type="text"
                    value={yHigh}
                    onChange={e => setYHigh(e.target.value)}
                    placeholder="High"
                    className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">↓ Bottom</label>
                  <input
                    type="text"
                    value={yLow}
                    onChange={e => setYLow(e.target.value)}
                    placeholder="Low"
                    className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Live mini preview */}
          <div className="p-3 bg-[var(--surface)] border">
            <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider mb-2">Matrix Preview</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-center">
              {QUADRANT_POSITIONS.map(pos => {
                const q = quadrants.find(qq => qq.position === pos.position);
                return (
                  <div
                    key={pos.position}
                    className={`${q?.headerBg ?? 'bg-[var(--hover-tint)]'} rounded p-2 border ${q?.border ?? 'border-[var(--rule)]'}`}
                  >
                    <div className="font-semibold text-[var(--text-pri)] truncate">{q?.name || pos.corner}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-[var(--text-ter)] mt-1 px-1">
              <span>← {xLow}</span>
              <span className="font-medium text-[var(--text-ter)]">{xAxisLabel}</span>
              <span>{xHigh} →</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Quadrants ────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
            <Info className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            <span>Name each quadrant, add an optional description, choose an action verb, and pick a colour.</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setAutoNames(true); }}
              className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              <RefreshCw className="h-3 w-3" />
              Reset to auto-generated names
            </button>
          </div>

          {QUADRANT_POSITIONS.map(pos => {
            const q = quadrants.find(qq => qq.position === pos.position)!;
            return (
              <div key={pos.position} className={` border-2 ${q.border} ${q.color} p-4 space-y-3`}>
                <div className={`-mx-4 -mt-4 px-4 py-2  ${q.headerBg} border-b ${q.border} flex items-center justify-between`}>
                  <div>
                    <span className="font-semibold text-sm text-[var(--text-pri)]">{pos.label}</span>
                    <span className="text-xs text-[var(--text-ter)] ml-2">({pos.corner})</span>
                  </div>
                  {/* Colour picker */}
                  <div className="flex items-center gap-1">
                    {COLOUR_OPTIONS.map(c => (
                      <button
                        key={c.label}
                        type="button"
                        title={c.label}
                        onClick={() => setQuadrantColor(pos.position, c)}
                        className={`w-4 h-4 rounded-full ${c.preview} border-2 transition-transform hover:scale-125 ${q.color === c.color ? 'border-[var(--rule)] scale-125' : 'border-transparent'}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-[var(--text-ter)] mb-1">Quadrant Name *</label>
                    <input
                      type="text"
                      value={q.name}
                      onChange={e => updateQuadrant(pos.position, 'name', e.target.value)}
                      placeholder="e.g. Quick Wins"
                      className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-ter)] mb-1">Description</label>
                    <input
                      type="text"
                      value={q.description}
                      onChange={e => updateQuadrant(pos.position, 'description', e.target.value)}
                      placeholder="e.g. High value, low cost"
                      className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-ter)] mb-1">Action Label</label>
                    <input
                      type="text"
                      value={q.action}
                      onChange={e => updateQuadrant(pos.position, 'action', e.target.value)}
                      placeholder="e.g. DO NOW"
                      className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-400"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Step 3: Save As ──────────────────────────────────── */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-[var(--text-pri)] mb-3">Where should this matrix be saved?</p>
            <ScopeSelector value={scope} onChange={setScope} />
          </div>

          {error && (
            <div className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
              {error}
            </div>
          )}

          <div className="p-3 bg-[var(--surface)] border text-sm text-[var(--text-sec)]">
            <span className="font-medium">Summary:</span> &ldquo;{name}&rdquo; with {xAxisLabel} × {yAxisLabel} axes
            will be saved {scope === 'local' ? 'locally in your browser' : scope === 'workspace' ? 'to the current workspace' : 'across the whole organization'}.
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-[var(--rule)]">
        <button
          type="button"
          onClick={step === 1 ? onCancel : () => setStep(prev => (prev - 1) as 1 | 2 | 3)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {step === 1 ? 'Cancel' : 'Back'}
        </button>

        {step < 3 ? (
          <button
            type="button"
            disabled={step === 1 ? !step1Valid : step === 2 ? !step2Valid : false}
            onClick={() => setStep(prev => (prev + 1) as 2 | 3)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 disabled:opacity-50 transition-colors"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : editTemplate ? 'Update Matrix' : 'Create & Use Matrix'}
            <CheckCircle2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Drag-and-drop item cards
// ============================================================================

// How an item moves without dragging: the runner gives every card its quadrants
// and a way to put the item in one (the phone's way — quadrants sit a screen
// away from the item, and a finger on a drag handle would scroll the page).
const MoveItemContext = createContext<{
  quadrants: QuadrantDefinition[];
  onMove: (id: string, quadrant: QuadrantKey) => void;
} | null>(null);

function DraggableCard({ item, onRemove }: { item: TaskItem; onRemove: (id: string) => void }) {
  const move = useContext(MoveItemContext);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 bg-[var(--bg-raised)] border border-[var(--rule)]  shadow-sm flex items-start gap-2 cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-blue-400' : ''
      }`}
    >
      {/* touch-none: without it a finger on the handle scrolls the page instead of dragging */}
      <div {...listeners} {...attributes} className="touch-none p-2 -m-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-[var(--text-pri)] truncate">{item.title}</div>
        {item.description && (
          <div className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-2">{item.description}</div>
        )}
        {move && (
          <select
            aria-label={`Move ${item.title} to`}
            value={item.quadrant ?? ''}
            onChange={e => move.onMove(item.id, e.target.value || null)}
            className="md:hidden mt-2 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-2 text-sm text-[var(--text-pri)]"
          >
            <option value="">Not placed</option>
            {move.quadrants.map(q => (
              <option key={q.key} value={q.key}>{q.name}</option>
            ))}
          </select>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); onRemove(item.id); }}
        className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1 flex-shrink-0"
        aria-label="Remove item"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function CardOverlay({ item }: { item: TaskItem }) {
  return (
    <div className="p-3 bg-[var(--bg-raised)] border-2 border-[var(--slate)] shadow-xl flex items-start gap-2 cursor-grabbing">
      <GripVertical className="w-4 h-4 text-[var(--text-ter)] flex-shrink-0" />
      <div className="flex-1 truncate">
        <div className="font-medium text-sm text-[var(--text-pri)]">{item.title}</div>
      </div>
    </div>
  );
}

function QuadrantZone({
  quadrant,
  items,
  onRemove,
}: {
  quadrant: QuadrantDefinition;
  items: TaskItem[];
  onRemove: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `quadrant-${quadrant.key}` });

  return (
    <div
      ref={setNodeRef}
      className={`${quadrant.color} border-2 ${
        isOver ? 'border-[var(--slate)] ring-2 ring-blue-200' : quadrant.border
      }  p-3 min-h-[200px] transition-all`}
    >
      <div className={`${quadrant.headerBg} -mx-3 -mt-3 px-3 py-2  mb-3`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-[var(--text-pri)] text-sm">{quadrant.name}</div>
            {quadrant.description && (
              <div className="text-xs text-[var(--text-sec)]">{quadrant.description}</div>
            )}
          </div>
          {quadrant.action && (
            <div className="text-xs font-bold text-[var(--text-sec)] bg-[var(--bg-raised)]/60 px-2 py-1 rounded">
              {quadrant.action}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 min-h-[120px]">
        {items.length === 0 && (
          <div
            className={`text-sm text-[var(--text-ter)] italic text-center py-8 border-2 border-dashed  ${
              isOver ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)]'
            }`}
          >
            Drop items here
          </div>
        )}
        {items.map(item => (
          <DraggableCard key={item.id} item={item} onRemove={onRemove} />
        ))}
      </div>
      {items.length > 0 && (
        <div className="mt-2 text-xs text-[var(--text-ter)] text-right">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

function UncategorizedZone({
  items,
  onRemove,
}: {
  items: TaskItem[];
  onRemove: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'quadrant-uncategorized' });
  if (items.length === 0) return null;

  return (
    <div
      ref={setNodeRef}
      className={`border-2 ${
        isOver ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--slate)] bg-[var(--slate-dim)]'
      }  p-4 transition-all`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-[var(--text-ter)]">Items to Place</h3>
          <p className="text-xs text-[var(--text-ter)]">Drag each item to the appropriate quadrant</p>
        </div>
        <span className="text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-1 rounded">
          {items.length} remaining
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map(item => (
          <DraggableCard key={item.id} item={item} onRemove={onRemove} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Scope badge
// ============================================================================

function ScopeBadge({ scope }: { scope: MatrixSaveScope }) {
  if (scope === 'local') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded-full">
        <HardDrive className="h-3 w-3" /> Local
      </span>
    );
  }
  if (scope === 'workspace') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-0.5 rounded-full">
        <Globe2 className="h-3 w-3" /> Workspace
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-0.5 rounded-full">
      <Building2 className="h-3 w-3" /> Organization
    </span>
  );
}

// ============================================================================
// Main runner — uses a selected template
// ============================================================================

function MatrixRunner({
  template,
  onChangeTemplate,
  hostProps,
}: {
  template: CustomMatrixTemplate;
  onChangeTemplate: () => void;
  hostProps: ToolHostProps;
}) {
  const situationalContext = useToolSituationalContext();
  const completeLabel = situationalContext?.completion?.label ?? 'Save Session Result';

  const [items, setItems] = useState<TaskItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const uncategorizedItems = items.filter(i => i.quadrant === null);
  const itemsInQuadrant = (key: string) => items.filter(i => i.quadrant === key);

  const handleRemove = (id: string) => setItems(prev => prev.filter(i => i.id !== id));
  const handleMove = (id: string, quadrant: QuadrantKey) =>
    setItems(prev => prev.map(i => (i.id === id ? { ...i, quadrant } : i)));

  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    setItems(prev => [
      ...prev,
      ...toolItems.map(item => ({
        id: item.id,
        title: item.name,
        description: item.description,
        quadrant: null,
        sourceId: item.sourceId,
      })),
    ]);
  }, []);

  const handleDragStart = (event: DragStartEvent) => setActiveId(String(event.active.id));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const overId = String(over.id);
    if (overId === 'quadrant-uncategorized') {
      setItems(prev => prev.map(i => (i.id === active.id ? { ...i, quadrant: null } : i)));
    } else if (overId.startsWith('quadrant-')) {
      const key = overId.replace('quadrant-', '');
      setItems(prev => prev.map(i => (i.id === active.id ? { ...i, quadrant: key } : i)));
    }
  };

  const handleGetResults = useCallback((): ToolResultItem[] => {
    const quadrantPriority: Record<string, number> = {};
    template.quadrants.forEach((q, i) => { quadrantPriority[q.key] = i + 1; });

    return items
      .filter(i => i.quadrant !== null)
      .map(i => ({
        id: i.sourceId || i.id,
        name: i.title,
        category: i.quadrant!,
        score: template.quadrants.length + 1 - (quadrantPriority[i.quadrant!] ?? template.quadrants.length),
        classificationBreakdown: { quadrant: i.quadrant! },
        metadata: {
          quadrantName: template.quadrants.find(q => q.key === i.quadrant)?.name,
          action: template.quadrants.find(q => q.key === i.quadrant)?.action,
        },
      }))
      .sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [items, template]);

  const activeItem = activeId ? items.find(i => i.id === activeId) : null;
  const allCategorized = items.length > 0 && uncategorizedItems.length === 0;

  return (
    <ToolWrapper
      toolName={template.name}
      toolDescription={template.description || `Custom 2×2 matrix: ${template.xAxisLabel} × ${template.yAxisLabel}`}
      onImportItems={handleImportItems}
      onGetResults={allCategorized ? handleGetResults : undefined}
      itemCount={items.length}
      {...hostProps}
    >
      <div className="space-y-6">
        {/* Template info bar */}
        <div className="flex items-center justify-between p-3 bg-[var(--surface)] border">
          <div className="flex items-center gap-3">
            <LayoutGrid className="h-5 w-5 text-[var(--text-ter)]" />
            <div>
              <span className="font-semibold text-[var(--text-pri)] text-sm">{template.name}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[var(--text-ter)]">
                  {template.xAxisLabel} × {template.yAxisLabel}
                </span>
                <ScopeBadge scope={template.scope} />
              </div>
            </div>
          </div>
          <button
            onClick={onChangeTemplate}
            className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] border border-[var(--rule)] rounded px-2 py-1 hover:bg-[var(--hover-tint)]"
          >
            <RefreshCw className="h-3 w-3" />
            Change Matrix
          </button>
        </div>

        <MoveItemContext.Provider value={{ quadrants: template.quadrants, onMove: handleMove }}>
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <UncategorizedZone items={uncategorizedItems} onRemove={handleRemove} />

          {items.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-[var(--surface)] border">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-[var(--text-pri)]">{items.length} total items</span>
                <span className="text-[var(--text-ter)]">|</span>
                <span className={uncategorizedItems.length > 0 ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                  {uncategorizedItems.length > 0
                    ? `${uncategorizedItems.length} unplaced`
                    : 'All items placed'}
                </span>
              </div>
              <button onClick={() => setItems([])} className="text-sm text-[var(--text-ter)] hover:text-[var(--coral)]">
                Clear All
              </button>
            </div>
          )}

          {items.length > 0 && (
            <>
              {/* Axis labels */}
              <div className="relative">
                {/* Y axis label */}
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full pr-2 hidden md:flex flex-col items-center gap-1">
                  <span className="text-xs text-[var(--text-ter)] [writing-mode:vertical-rl] rotate-180">{template.yAxisLabel}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Top row */}
                  {(['top-left', 'top-right'] as const).map(pos => {
                    const q = template.quadrants.find(qq => qq.position === pos)!;
                    return (
                      <QuadrantZone key={pos} quadrant={q} items={itemsInQuadrant(q.key)} onRemove={handleRemove} />
                    );
                  })}
                  {/* Bottom row */}
                  {(['bottom-left', 'bottom-right'] as const).map(pos => {
                    const q = template.quadrants.find(qq => qq.position === pos)!;
                    return (
                      <QuadrantZone key={pos} quadrant={q} items={itemsInQuadrant(q.key)} onRemove={handleRemove} />
                    );
                  })}
                </div>

                {/* X axis label */}
                <div className="flex justify-between items-center mt-2 px-1 text-xs text-[var(--text-ter)]">
                  <span>← {template.xAxisLowLabel}</span>
                  <span className="font-medium text-[var(--text-ter)]">{template.xAxisLabel}</span>
                  <span>{template.xAxisHighLabel} →</span>
                </div>
              </div>
            </>
          )}

          <DragOverlay>
            {activeItem ? <CardOverlay item={activeItem} /> : null}
          </DragOverlay>
        </DndContext>
        </MoveItemContext.Provider>

        {allCategorized && (
          <div className="card bg-[var(--green-dim)] border-[var(--green)]">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-[var(--green)] flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">All items placed!</h3>
                <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                  Click <strong>"{completeLabel}"</strong> in the header to capture this matrix run.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolWrapper>
  );
}

// ============================================================================
// Template library (landing view)
// ============================================================================

function TemplateLibrary({
  templates,
  onSelect,
  onCreateNew,
  onEdit,
  onDelete,
  loading,
}: {
  templates: CustomMatrixTemplate[];
  onSelect: (t: CustomMatrixTemplate) => void;
  onCreateNew: () => void;
  onEdit: (t: CustomMatrixTemplate) => void;
  onDelete: (id: string) => void;
  loading: boolean;
}) {
  return (
    <ToolWrapper
      toolName="Custom 2×2 Matrix"
      toolDescription="Build, name, and save your own 2×2 prioritization matrix"
      itemCount={-1}
      showZeroState={false}
    >
      <div className="space-y-6">
        {/* Hero */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-[var(--text-pri)]">Your Custom Matrices</h2>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              Create any 2×2 matrix with your own axis labels and quadrant names, then save it locally,
              to your workspace, or across your organization.
            </p>
          </div>
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create New Matrix
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-2 border-[var(--rule)] border-t-consulting-slate-blue-600 rounded-full mx-auto mb-3" />
              <p className="text-sm">Loading your matrices…</p>
            </div>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 bg-[var(--hover-tint)] rounded-full mb-4">
              <LayoutGrid className="h-8 w-8 text-[var(--text-ter)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-sec)] mb-1">No custom matrices yet</h3>
            <p className="text-sm text-[var(--text-ter)] max-w-sm">
              Create your first custom 2×2 matrix. Define the axes and quadrant names that matter to your team.
            </p>
            <button
              onClick={onCreateNew}
              className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-consulting-slate-blue-600 border border-consulting-slate-blue-300 hover:bg-consulting-slate-blue-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Your First Matrix
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(t => (
              <div
                key={t.id}
                className="group relative border border-[var(--rule)] p-4 hover:border-consulting-slate-blue-300 hover:shadow-sm transition-all bg-[var(--bg-raised)]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="font-semibold text-[var(--text-pri)] truncate">{t.name}</h3>
                    {t.description && (
                      <p className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-2">{t.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-[var(--text-ter)]">
                        {t.xAxisLabel} × {t.yAxisLabel}
                      </span>
                      <ScopeBadge scope={t.scope} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onEdit(t)}
                      className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)]"
                      title="Edit this matrix"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(t.id)}
                      className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] rounded hover:bg-[var(--coral-dim)]"
                      title="Delete this matrix"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Mini 2×2 preview */}
                <div className="grid grid-cols-2 gap-1 text-xs mb-3">
                  {QUADRANT_POSITIONS.map(pos => {
                    const q = t.quadrants.find(qq => qq.position === pos.position);
                    return (
                      <div
                        key={pos.position}
                        className={`${q?.headerBg ?? 'bg-[var(--hover-tint)]'} rounded p-1.5 border ${q?.border ?? 'border-[var(--rule)]'} text-center`}
                      >
                        <div className="font-medium text-[var(--text-pri)] truncate">{q?.name ?? '—'}</div>
                        {q?.action && <div className="text-[var(--text-ter)] text-[10px]">{q.action}</div>}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => onSelect(t)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-consulting-slate-blue-600 bg-consulting-slate-blue-50 hover:bg-consulting-slate-blue-100 transition-colors"
                >
                  Use This Matrix
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </ToolWrapper>
  );
}

// ============================================================================
// Root component
// ============================================================================

type ViewMode = 'library' | 'builder' | 'edit' | 'runner';

function CustomMatrixContent(props: ToolHostProps) {
  const userId = useUserStore(s => s.userId);
  const workspaceId = useTenantStore(s => s.currentWorkspaceId) ?? undefined;
  const currentTenantId = useTenantStore(s => s.currentTenantId) ?? undefined;

  const { templates, loading, fetchTemplates, deleteTemplate } = useCustomMatrixStore();

  const [view, setView] = useState<ViewMode>('library');
  const [selectedTemplate, setSelectedTemplate] = useState<CustomMatrixTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<CustomMatrixTemplate | undefined>(undefined);

  useEffect(() => {
    fetchTemplates(workspaceId, currentTenantId);
  }, [fetchTemplates, workspaceId, currentTenantId]);

  // /prioritization/tools/custom-matrix/:templateId opens that matrix once the
  // templates have loaded. Applied once per id, so going back to the library
  // on this page still works; an id that is not in the library stays on it.
  const { templateId: routeTemplateId } = useParams<{ templateId?: string }>();
  const [openedRouteTemplateId, setOpenedRouteTemplateId] = useState<string | null>(null);
  useEffect(() => {
    if (!routeTemplateId || routeTemplateId === openedRouteTemplateId) return;
    const t = templates.find(x => x.id === routeTemplateId);
    if (!t) return;
    setOpenedRouteTemplateId(routeTemplateId);
    setSelectedTemplate(t);
    setView('runner');
  }, [routeTemplateId, openedRouteTemplateId, templates]);

  const handleTemplateCreated = (t: CustomMatrixTemplate) => {
    setSelectedTemplate(t);
    setView('runner');
  };

  const handleEdit = (t: CustomMatrixTemplate) => {
    setEditingTemplate(t);
    setView('edit');
  };

  const handleEditSaved = (updated: CustomMatrixTemplate) => {
    setEditingTemplate(undefined);
    setView('library');
    // If currently running this template, update it
    if (selectedTemplate?.id === updated.id) {
      setSelectedTemplate(updated);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this custom matrix? This cannot be undone.')) return;
    await deleteTemplate(id);
  };

  if (view === 'builder') {
    return (
      <ToolWrapper
        toolName="Create Custom Matrix"
        toolDescription="Define your own 2×2 prioritization matrix"
        itemCount={-1}
        showZeroState={false}
        backTo={{ label: 'My Matrices', onClick: () => setView('library') }}
        {...props}
      >
        <div className="max-w-2xl mx-auto py-4">
          <MatrixBuilder
            onSave={handleTemplateCreated}
            onCancel={() => setView('library')}
            workspaceId={workspaceId}
            orgId={currentTenantId}
            userId={userId}
          />
        </div>
      </ToolWrapper>
    );
  }

  if (view === 'edit' && editingTemplate) {
    return (
      <ToolWrapper
        toolName={`Edit: ${editingTemplate.name}`}
        toolDescription="Update your custom matrix definition"
        itemCount={-1}
        showZeroState={false}
        backTo={{ label: 'My Matrices', onClick: () => setView('library') }}
        {...props}
      >
        <div className="max-w-2xl mx-auto py-4">
          <MatrixBuilder
            editTemplate={editingTemplate}
            onSave={handleEditSaved}
            onCancel={() => setView('library')}
            workspaceId={workspaceId}
            orgId={currentTenantId}
            userId={userId}
          />
        </div>
      </ToolWrapper>
    );
  }

  if (view === 'runner' && selectedTemplate) {
    return (
      <MatrixRunner
        template={selectedTemplate}
        onChangeTemplate={() => setView('library')}
        hostProps={{
          ...props,
          backTo: { label: 'My Matrices', onClick: () => setView('library') },
        }}
      />
    );
  }

  return (
    <TemplateLibrary
      templates={templates}
      loading={loading}
      onSelect={t => { setSelectedTemplate(t); setView('runner'); }}
      onCreateNew={() => setView('builder')}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );
}

export default function CustomMatrix(props: ToolHostProps) {
  return <CustomMatrixContent {...props} />;
}
